// Токены доступа к корзине и заказу по id (пакет безопасности A, 17.10.2026).
//
// Шлюз отдаёт заказ целиком только владельцу (токен входа) или по токену
// заказа, а чужую корзину аккаунта — не отдаёт вовсе. Токен выдаётся в ответе
// при создании корзины/заказа (`accessToken`); витрина хранит его здесь и шлёт
// заголовками X-Cart-Token / X-Order-Token. Ссылка на оплату от сервера несёт
// токен во фрагменте (`/pay/<id>#t=…`) — его забирает страница оплаты.

const KEY = "osmix_access_tokens";
const LIMIT = 50;

type Kind = "cart" | "order";
type Store = Record<string, { t: string; at: number }>;

let memory: Store | null = null;

const read = (): Store => {
	if (memory) return memory;
	try {
		const raw = typeof window !== "undefined" ? window.localStorage.getItem(KEY) : null;
		memory = raw ? (JSON.parse(raw) as Store) : {};
	} catch {
		memory = {};
	}
	return memory;
};

const write = (store: Store) => {
	memory = store;
	try {
		window.localStorage.setItem(KEY, JSON.stringify(store));
	} catch {
		// приватный режим — токены живут до перезагрузки
	}
};

export const getAccessToken = (kind: Kind, id: string): string | null => read()[`${kind}:${id}`]?.t ?? null;

export const saveAccessToken = (kind: Kind, id: string, token: unknown): void => {
	if (!id || typeof token !== "string" || !token) return;
	const store = { ...read() };
	if (store[`${kind}:${id}`]?.t === token) return;
	store[`${kind}:${id}`] = { t: token, at: Date.now() };
	// Храним последние LIMIT — старые заказы и корзины не нужны.
	const keys = Object.keys(store).sort((a, b) => (store[b]?.at ?? 0) - (store[a]?.at ?? 0));
	for (const k of keys.slice(LIMIT)) delete store[k];
	write(store);
};

/** Заголовки доступа для запроса к корзине/заказу. */
export const accessHeaders = (kind: Kind, id: string): Record<string, string> | undefined => {
	const token = getAccessToken(kind, id);
	if (!token) return undefined;
	return kind === "cart" ? { "X-Cart-Token": token } : { "X-Order-Token": token };
};

/** Запомнить токены из ответа шлюза: корзина или заказ (с заказами того же оформления). */
export const rememberCartToken = (raw: unknown): void => {
	const r = raw as { id?: string; accessToken?: string } | null;
	if (r?.id) saveAccessToken("cart", r.id, r.accessToken);
};

export const rememberOrderTokens = (raw: unknown): void => {
	const r = raw as {
		id?: string;
		accessToken?: string;
		checkoutOrders?: { id?: string; accessToken?: string }[];
	} | null;
	if (!r?.id) return;
	saveAccessToken("order", r.id, r.accessToken);
	for (const o of r.checkoutOrders ?? []) if (o?.id) saveAccessToken("order", o.id, o.accessToken);
};

/** Токен из фрагмента ссылки на оплату (`#t=…`): запомнить и убрать из адреса. */
export const takeOrderTokenFromHash = (orderId: string): void => {
	if (typeof window === "undefined" || !orderId) return;
	const hash = window.location.hash.replace(/^#/, "");
	if (!hash) return;
	const params = new URLSearchParams(hash);
	const token = params.get("t");
	if (!token) return;
	saveAccessToken("order", orderId, token);
	params.delete("t");
	const rest = params.toString();
	try {
		window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ""}`);
	} catch {
		// адрес останется с токеном — не страшно, во фрагменте он не уходит на сервер
	}
};
